/**
 * SA-759: every word the Mailboxes page says about a backload. Refusals are
 * worded here from their codes (a Map, so a code such as `constructor`
 * cannot reach a prototype member); a bare code is never printed.
 */
import { ApiError } from '@/services/apiClient'
import type {
  BackloadMode, BackloadPreview, BackloadRange, Mailbox, MailboxBackload, MailboxChannel, SinkType,
} from '@/services/mailboxes'

/** SA-947 (spec P10): the backload's words for a mailbox's channel. The
 *  email words are SA-759's, byte for byte. */
export interface BackloadWords {
  title: string
  description: (displayName: string) => string
  modeLegend: string
  footnote: string
  liveLabel: string
  routerLiveWarning: string
  earlier: string
  emptyRange: string
  subject: string
  /** SA-1079: the row's cancel control and its progress bar's name. */
  cancel: string
  progressLabel: string
}

const MAIL_WORDS: BackloadWords = {
  title: 'Load earlier mail',
  description: (name) => `${name}: bring mail received before the mailbox was connected into the Inbox.`,
  modeLegend: 'What runs on this mail',
  footnote: 'Messages keep the time they were received. No reply is drafted or sent for earlier mail.',
  liveLabel: 'Handle as new mail',
  routerLiveWarning: "Each decided kind's handler runs as it would on new mail: records are created and processes started, with due times counted from today, and imports such as schedule revisions are made.",
  earlier: 'Earlier mail',
  emptyRange: 'No inbox mail in this range.',
  subject: 'the mailbox',
  cancel: 'Cancel load',
  progressLabel: 'Earlier mail loaded',
}

const GROUP_WORDS: BackloadWords = {
  title: 'Load earlier messages',
  description: (name) => `${name}: bring messages posted in the group before it was bound into the Inbox.`,
  modeLegend: 'What runs on these messages',
  footnote: 'Messages keep the time they were sent. Nothing is posted to the group.',
  liveLabel: 'Handle as new messages',
  routerLiveWarning: "Each decided kind's handler runs as it would on a new message: records are created and processes started, with due times counted from today, and imports such as schedule revisions are made.",
  earlier: 'Earlier messages',
  emptyRange: 'No group messages in this range.',
  subject: 'the source',
  cancel: 'Cancel load',
  progressLabel: 'Earlier messages loaded',
}

export function backloadWords(channel?: MailboxChannel): BackloadWords {
  return channel === 'whatsapp-group' ? GROUP_WORDS : MAIL_WORDS
}

const REFUSALS = new Map<string, string>([
  ['mailbox_not_connected', 'Connect the mailbox before loading its earlier mail.'],
  ['backload_range_invalid', 'The range must end after it starts.'],
  ['backload_range_too_wide', 'Choose a range of 90 days or fewer.'],
  ['backload_too_many_messages', 'That range holds more than 5,000 messages. Choose a shorter range.'],
  ['backload_in_progress', 'Earlier mail is already loading on this mailbox. Wait for it to finish, or cancel it.'],
  ['propose_needs_router', 'Propose needs a router mailbox: only a router makes decisions to hold for a person.'],
  ['backload_mode_invalid', 'Choose what runs on this mail.'],
  ['backload_not_loading', 'That load has already finished.'],
  ['backload_not_found', 'That load is not on this mailbox.'],
  ['mailbox_provider_error', 'The mailbox provider did not answer. Try again.'],
  ['mailbox_auth_failed', 'The mailbox refused our sign-in. Reconnect it, then try again.'],
])

const FALLBACK = 'The request did not complete. Try again.'

export function backloadErrorText(err: unknown): string {
  if (err instanceof ApiError && err.detail && typeof err.detail === 'object' && !Array.isArray(err.detail)) {
    const detail = err.detail as { code?: unknown; message?: unknown }
    const known = typeof detail.code === 'string' ? REFUSALS.get(detail.code) : undefined
    if (known) return known
    if (typeof detail.message === 'string' && detail.message.trim()) return detail.message
  }
  return FALLBACK
}

/** What a run's `error` code means, as part of a sentence. */
const RUN_ERRORS = new Map<string, string>([
  ['backload_batch_failed', 'messages kept failing'],
  ['backload_provider_error', 'the provider did not answer'],
  ['backload_internal_error', 'an internal error'],
  // SA-1004: only a WhatsApp group source writes it (the deployment's WAHA key).
  ['backload_auth_failed', 'WAHA refused the API key'],
])

function runError(code: string | null): string {
  return (code && RUN_ERRORS.get(code)) || 'the load failed'
}

export interface ModeOption {
  mode: BackloadMode
  label: string
  description: string
  /** What `live` will run on this sink; null when it runs nothing more. */
  warning: string | null
}

const LIVE_WARNING: Record<SinkType, string | null> = {
  router: "Each decided kind's handler runs as it would on new mail: records are created and processes started, with due times counted from today, and imports such as schedule revisions are made.",
  record: 'A record is created from each new thread and its process starts, with due times counted from today.',
  workflow: 'The workflow runs on each message as if it had just arrived.',
  conversation: null,
}

export function modeOptions(sinkType: SinkType, words: BackloadWords = MAIL_WORDS): ModeOption[] {
  const options: ModeOption[] = [{
    mode: 'capture',
    label: 'Capture only',
    description: 'Conversations and attachments only. Nothing is classified and nothing else runs.',
    warning: null,
  }]
  if (sinkType === 'router') {
    options.push({
      mode: 'propose',
      label: 'Propose',
      description: 'Each message is classified and waits for a person to approve it. No handler runs.',
      warning: null,
    })
  }
  options.push({
    mode: 'live',
    label: words.liveLabel,
    description: 'Each message is handled as if it had just arrived.',
    warning: sinkType === 'router' ? words.routerLiveWarning : LIVE_WARNING[sinkType],
  })
  return options
}

/** SA-986 (design note D2, D3): classify-ahead applies on an email router run
 *  that classifies; the server stores false anywhere else. */
export function classifyAheadOffered(
  mailbox: Pick<Mailbox, 'sink_type'> & { channel?: Mailbox['channel'] }, mode: BackloadMode,
): boolean {
  return mailbox.sink_type === 'router' && (mailbox.channel ?? 'email') === 'email' && mode !== 'capture'
}

export const CLASSIFY_AHEAD_WORDS = {
  label: 'Classify ahead',
  description: "Runs the model on a chunk's messages at once, then writes them in order. Faster on a long range.",
}

function extras(run: MailboxBackload): string[] {
  const files = run.files_missing ?? 0
  return [
    run.classify_ahead ? 'classify ahead' : null,
    run.skipped ? `${run.skipped} skipped` : null,
    run.parked ? `${run.parked} parked` : null,
    files ? `${files} ${files === 1 ? 'file' : 'files'} not loaded` : null,
  ].filter((part): part is string => part !== null)
}

function waiting(mailboxStatus?: Mailbox['status'], words: BackloadWords = MAIL_WORDS): string[] {
  if (!mailboxStatus || mailboxStatus === 'active') return []
  return [mailboxStatus === 'paused' ? `waiting: ${words.subject} is paused` : `waiting: reconnect ${words.subject}`]
}

/** Counts group thousands (`1,480`): the walkers read a bare `1480` as a code. */
function count(n: number): string {
  return n.toLocaleString('en-US')
}

/** SA-1079: the drain loads one chunk per poll tick, and the first chunk waits
 * for the first tick after Start, which is what a person sees as nothing
 * happening. */
function nextChunk(run: MailboxBackload): string {
  return run.position === 0 && !run.started_at ? 'first chunk on the next poll' : 'next chunk on the next poll'
}

export function backloadLine(
  run: MailboxBackload, mailboxStatus?: Mailbox['status'], words: BackloadWords = MAIL_WORDS,
): string {
  switch (run.status) {
    case 'loading': {
      const held = waiting(mailboxStatus, words)
      const state = held.length ? held : [run.error ? `retrying (${runError(run.error)})` : nextChunk(run)]
      return [
        `Loading ${words.earlier.toLowerCase()} · ${count(run.position)} of ${count(run.total)}`,
        ...extras(run),
        ...state,
      ].join(' · ')
    }
    case 'done':
      return [`${words.earlier} loaded: ${run.loaded}`, `${run.already_present} already here`, ...extras(run)].join(' · ')
    case 'failed':
      return [`${words.earlier} stopped: ${runError(run.error)}`, `${run.loaded} loaded`, ...extras(run)].join(' · ')
    case 'cancelled':
      return [
        run.cancelled_by_name ? `${words.earlier} canceled by ${run.cancelled_by_name}` : `${words.earlier} canceled`,
        `${run.loaded} loaded`, ...extras(run)].join(' · ')
  }
}

export function previewLine(preview: BackloadPreview, words: BackloadWords = MAIL_WORDS): string {
  if (preview.found === 0) return words.emptyRange
  if (preview.to_load === 0) {
    return `Nothing to load: all ${preview.found} messages in this range are already here.`
  }
  return `Found ${preview.found} · ${preview.already_present} already here · ${preview.to_load} to load`
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

function localDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** Local calendar dates (YYYY-MM-DD) → the API's range: the start day's
 *  first instant to the first instant of the day AFTER the end day, so the
 *  end day is included. Built from local parts, so a DST change cannot
 *  shift a day. */
export function rangeFromDates(startDate: string, endDate: string): BackloadRange {
  const [sy, sm, sd] = startDate.split('-').map(Number)
  const [ey, em, ed] = endDate.split('-').map(Number)
  return {
    start: new Date(sy, sm - 1, sd).toISOString(),
    end: new Date(ey, em - 1, ed + 1).toISOString(),
  }
}

/** The dialog's default: two weeks ago through today. */
export function defaultDates(now: Date): { start: string; end: string } {
  return {
    start: localDate(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 14)),
    end: localDate(now),
  }
}
